import React, { useState } from "react";

import { Modal } from "../../../common/Modal";
import {
  BuildingIcon,
  ShieldIcon,
  MapPinIcon,
} from "../../../../assets/ui/icons";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { MembershipVerifyNotice } from "./MembershipVerifyNotice";
import { SectionAddBar } from "./SectionAddBar";
import { DetailCard } from "./DetailCard";
import { BankFormModal } from "./BankFormModal";
import { VerificationModal } from "./VerificationModal";
import { useBankingSection } from "./useBankingSection";
import { sectionRowKey, sectionStatus } from "./sectionDrafts";
import { BANK_ROWS, BANK_SPEC, bankRowValues } from "./bankingFields";

/* Section 13 — Banking Information. */

/* `bank_letter` is the upload endpoint's own category for this document, and
   the path the live BankingDetail uses. */
const VERIFY_SPEC = {
  uploadPath: uploadFilePath?.bank,
  category: "bank_letter",
};

const ROW_ICONS = {
  bank_name: BuildingIcon,
  IBAN: ShieldIcon,
  country: MapPinIcon,
};

export const BankingInformationSection = ({
  onNext,
  onPrev,
  onSkip,
  onStatusChange,
  header,
  className = "",
}) => {
  const { rows, remove, refresh } = useBankingSection({ onStatusChange });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const cardRows = (record) => {
    const values = bankRowValues(record);
    return BANK_ROWS.map((row) => ({
      key: row.key,
      label: row.label,
      icon: ROW_ICONS[row.key],
      value: values[row.key],
    }));
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={<EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />}
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Banking Information
      </h2>

      {/* Members only — see MembershipVerifyNotice. The offer says the
          verification is free WITH YOUR MEMBERSHIP, so it is not shown to
          anyone who does not have one. */}
      <MembershipVerifyNotice onUpload={() => setVerifyOpen(true)} />

      <SectionAddBar
        className="mt-4"
        prompt="Add your Banking Information"
        label="Add Banking Information"
        onAdd={() => {
          setEditing(null);
          setFormOpen(true);
        }}
      />

      {rows.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rows.map((record) => (
            <DetailCard
              key={sectionRowKey(record, BANK_SPEC)}
              status={sectionStatus(record)}
              rows={cardRows(record)}
              /* No onEdit: see the note above. DetailCard renders a single
                 full-width Delete when there is nothing to pair it with. */
              onDelete={() => remove(record)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <Modal modalHandle={closeForm} noClose bare>
          <BankFormModal
            editing={editing}
            onClose={closeForm}
            onSaved={() => {
              closeForm();
              refresh();
            }}
          />
        </Modal>
      )}

      {verifyOpen && (
        <Modal modalHandle={() => setVerifyOpen(false)} noClose bare>
          <VerificationModal
            spec={VERIFY_SPEC}
            onClose={() => setVerifyOpen(false)}
            onDone={() => {
              setVerifyOpen(false);
              refresh();
            }}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default BankingInformationSection;
